/**
 * Возраст по дню рождения (на сегодня или на дату)
 * ====================================
 * Не подходит для Кореи, там свой расчет возраста
 *
 * URLs:
 * Indexed:
 * - age (index)
 * - age/if-born-in-january-2024
 * - age/if-born-in-2024
 * Not indexed as pointless:
 * - age/if-born-january-15-2024
 * - age/at-january-15-2025-if-born-january-15-2024
 *
 * Калькулятор требует настройки robots.txt, чтобы не индексировать страницы со слишком специфичным датами
 * TODO: код для вычисления на JS
 * TODO Кастомизировать страницу под личность
 * TODO Для страниц с годом, выдать список личностей, родившихся в этот год
 * TODO Optimize image alt and caption: https://seosherpa.com/image-seo/
 * TODO Add images to sitemap!!!!
 * TODO Add open graph
 * TODO How old if born in specific month? Give results!
 * TODO Add "Popular requests" section with links to most weighted longtails
 * TODO Статистика возраста
 *  Численность населения РФ в этом возрастном периоде (60-64 лет) составляет 4336 тысяч человек. Из них мужчин 1771 тыс. человек, женщин 2565 тыс. человек. Из населения данной возрастной группы занятых в экономике РФ всего 3,3%
 * TODO ВЫ прожили % от среднего в регионе Россия/Мир
 * TODO Убрать бы дейтпикер. Мне вот нравится как ввод сделан: https://agecalc.io/
 * TODO Посмори почему эти страницы в топе: https://agecalc.io/year/1992/
 *  https://smr3.noxtools.com/analytics/organic/positions/?sortField=&sortDirection=desc&db=us&q=agecalc.io&searchType=domain&date=20240529
 *
 * TODO Gemini считает, что на странице недостаточно EEAT факторов:
 * https://gemini.google.com/app/7feb08e7de72f458
 */
import fs from "node:fs";

import { LoaderFunctionArgs, MetaFunction } from "react-router";
import { Link, useLoaderData, useLocation } from "react-router";
import { Jsonify } from "@react-router/server-runtime/dist/jsonify";
import { TFunction } from "i18next";
import React, { FormEvent } from "react";
import { useTranslation } from "react-i18next";
import invariant from "tiny-invariant";

import { addNoindexMeta, ShouldIndexMeta } from "~/helpers/meta-noindex";
import { getBaseUrl } from "~/helpers/getBaseUrl";
import ArticleImage from "~/components/ArticleImage";
import Button from "~/components/Button";
import DatePicker from "~/components/forms/DatePicker";
import H2 from "~/components/H2";
import Icon from "~/components/Icon";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Author from "~/components/tool-page/Author";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";

import {
  generationByYearOfBirth,
  getChineseZodiac,
  getZodiacSign
} from "~/helpers/dates.server";
import { celebrities } from "~/helpers/numbers/age.server";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, createFAQSchema } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import { formatLongDate, getDatesDiff } from "~/helpers/dates";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";

const CATEGORY = "dates";
const TKEY = "calculators.dates.age";
const ROUTE_ENDING = "age";
// Use a fixed ISO string to ensure consistent timestamp between server and client
const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = function ({ data }) {
  invariant(data);

  const meta = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      data?.meta.images ?? [],
      LAST_UPDATED
    ),
    // Open graph
    { property: "og:title", content: makeTitle(data?.meta.title) },
    { property: "og:type", content: "website" },
    { property: "og:url", content: data?.meta.currentUrl },
    { property: "og:image", content: data?.meta.images[0] ?? '' },
    { property: "og:description", content: data?.meta.description },
    { property: "og:locale", content: data?.meta.locale },
  ];

  addNoindexMeta(meta, data.meta.shouldIndex);

  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }

  const faqSchema = createFAQSchema(data.faq ?? []);
  if (faqSchema) meta.push(faqSchema);

  return meta;
}

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"],
  i18n: ["translations", "dates"],
};

interface Query {
  birthday: Date;
  at: Date | null;
  precision: "date" | "month" | "year";
}

interface Result {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalMonths: number;
  nextBirthday: Date;
  monthsTillNextBirthday: number;
  daysTillNextBirthday: number;
  precision: "date" | "month" | "year";
  generation: string | null;
  zodiacSign: string | null;
  chineseZodiacSign: string;
}

class InvalidQueryError extends Error {
}

function parseInput(input: string): Query {
  let atDayStr: string | null = null;
  let atMonthName: string | null = null;
  let atYearStr: string | null = null;
  let birthDayStr: string | null = null;
  let birthMonthName: string | null = null;
  let birthYearStr: string | null = null;
  let precision: "date" | "year" | "month" = "date";

  switch (true) {
    // if-born-in-1997
    case /^if-born-in-\d{4}$/.test(input): {
      const match = input.match(/^if-born-in-(\d{4})$/);
      invariant(match);

      birthYearStr = match[1];
      precision = "year";

      return {
        birthday: new Date(parseInt(birthYearStr), 0, 1),
        at: null,
        precision: "year"
      };
    }

    // if-born-in-january-1997
    case /^if-born-in-(\w+)-(\d{4})$/.test(input): {
      const match = input.match(/^if-born-in-(\w+)-(\d{4})$/);
      invariant(match);

      birthMonthName = match[1];
      birthYearStr = match[2];
      invariant(birthMonthName);
      invariant(birthYearStr);

      const birthday = new Date(
        parseInt(birthYearStr),
        new Date(Date.parse(`01 ${birthMonthName} 2000`)).getMonth(),
        1,
        0, 0, 0, 0
      );
      return {
        birthday,
        at: null,
        precision: "month"
      };
    }

    case input.startsWith("at-"): {
      const regex = /^at-(\w+)-(\d+)-(\d{4})-if-born-(\w+)-(\d+)-(\d{4})$/;
      const match = input.match(regex);

      if (!match) {
        console.error("Invalid input format");
        throw new InvalidQueryError();
      }

      atDayStr = match[2];
      atMonthName = match[1];
      atYearStr = match[3];
      birthDayStr = match[5];
      birthMonthName = match[4];
      birthYearStr = match[6];
      break;
    }

    default: {
      const regex = /^if-born-(\w+)-(\d+)-(\d{4})$/;
      const match = input.match(regex);

      if (!match) {
        console.error("Invalid input format");
        throw new InvalidQueryError();
      }

      birthDayStr = match[2];
      birthMonthName = match[1];
      birthYearStr = match[3];
    }
  }

  const birthDay = birthDayStr === null ? 1 : parseInt(birthDayStr, 10);
  const birthMonth = birthMonthName === null ? 1 : new Date(Date.parse(`01 ${birthMonthName} 2000`)).getMonth() + 1;
  const birthYear = parseInt(birthYearStr, 10);

  const birthday = new Date(birthYear, birthMonth - 1, birthDay);
  if (isNaN(birthday.getTime())) {
    console.error("Invalid date");
    throw new InvalidQueryError();
  }

  if (atDayStr !== null && atMonthName !== null && atYearStr !== null) {
    const atDay = parseInt(atDayStr, 10);
    const atMonth = new Date(Date.parse(`01 ${atMonthName} 2000`)).getMonth() + 1;
    const atYear = parseInt(atYearStr, 10);

    const at = new Date(atYear, atMonth - 1, atDay);
    if (isNaN(at.getTime())) {
      console.error("Invalid date");
      throw new InvalidQueryError();
    }

    return { birthday, at, precision };
  }

  return { birthday, at: null, precision };
}

function createSlug(birthday: Date, at: Date | null = null): string {
  let slug = "";
  if (at !== null) {
    slug += `at-${at.toLocaleString("en", { month: "long" })}-${at.getDate()}-${at.getFullYear()}-`;
  }

  return `${slug}if-born-${birthday.toLocaleString("en", { month: "long" })}-${birthday.getDate()}-${birthday.getFullYear()}`.toLowerCase();
}

function createTitleFromQuery(query: Query, locale: string, t: TFunction): string {
  switch (query.precision) {
    case "year":
      return t(`${TKEY}.title_at_year`, { year: query.birthday.getFullYear() });
    case "month":
      return t(`${TKEY}.title_at_month_and_year`, {
        monthAndYear: locale === "ru"
          ? query.birthday.getFullYear() + " (" + query.birthday.toLocaleString(locale, { month: "long" }) + ")"
          : query.birthday.toLocaleString(locale, { month: "long", year: "numeric" })
      });
    case "date":
      if (query.at !== null) {
        return t(`${TKEY}.title_at_date`, {
          birthday: formatLongDate(query.birthday, locale),
          at: formatLongDate(query.at, locale)
        }) + `?`;
      } else {
        return t(`${TKEY}.title_at_today`, { birthday: formatLongDate(query.birthday, locale) }) + "?";
      }
    default:
      throw new Error("Invalid precision");
  }
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url) ?? "";

  let query: Query | null = null;

  try {
    query = getQueryFromPath((new URL(request.url)).pathname);
  } catch (e) {
    if (e instanceof InvalidQueryError) {
      throw new Response(null, {
        status: 404,
        statusText: "Not Found"
      });
    }
  }

  const t = await getTranslator(request);

  // Calculate result if query is set

  const diffMilliSeconds: number | null = query === null
    ? null
    : (query.at ?? new Date()).getTime() - query.birthday.getTime()
  ;

  const title = query ? createTitleFromQuery(query, locale, t) : t(`${TKEY}.title`);

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];


  let result: Result | null = null;

  const metaImages = [];

  let metaDescription: string = t(`${TKEY}.description`);

  let meaningOfAgeContent: string|null = null;

  let shouldIndex: ShouldIndexMeta = {
    googlebot: query === null,
    yandex: true,
    bingbot: true,
  };

  if (query !== null) {
    switch (query.precision) {
      case "year":
        metaDescription = t(`${TKEY}.description_at_month_and_year`, { date: query.birthday.getFullYear() });
        break;
      case "month":
        metaDescription = t(`${TKEY}.description_at_month_and_year`, {
          date: query.birthday.toLocaleString(locale, { month: "long", year: "numeric" })
        });
        break;
      case "date":
        // Do not index pages for specific dates
        shouldIndex.yandex = false;
        shouldIndex.bingbot = false;
        metaDescription = t(`${TKEY}.description_at_date`, { date: formatLongDate(query.birthday, locale) });
        break;
    }

    // No indexing for dates before 1900 and after 2030
    // No indexing when not for today date
    if (
      shouldIndex.yandex &&  (
        query.at !== null
      || query.birthday.getFullYear() < 1900
      || query.birthday.getFullYear() > 2030
      )
    ) {
      shouldIndex.yandex = false;
      shouldIndex.bingbot = false;
    }

    const slug = createSlug(query.birthday, query.at);
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${slug}` });

    const diff = getDatesDiff(query.birthday, query.at ?? new Date());


    // Create next birthday date
    const nextBirthday = new Date(new Date().getFullYear(), query.birthday.getMonth(), query.birthday.getDate());

    // If next birthday is before the current date, set it to the next year
    if (nextBirthday < new Date()) {
      nextBirthday.setFullYear(nextBirthday.getFullYear() + 1);
    }

    const diffTillNextBirthday = getDatesDiff(nextBirthday, new Date());

    const generation = generationByYearOfBirth(query.birthday.getFullYear());

    try {
      meaningOfAgeContent = fs.readFileSync(`content/dates/age/${locale}/meaning-${diff.years}.html`, 'utf8');
    } catch(e) {
      console.log('Error:', e);
    }


    result = {
      years: diff.years,
      months: diff.months,
      days: diff.days,
      totalDays: diff.totalDays,
      totalMonths: diff.totalMonths,
      nextBirthday,
      monthsTillNextBirthday: diffTillNextBirthday.months,
      daysTillNextBirthday: diffTillNextBirthday.days,
      precision: query.precision,
      generation,
      zodiacSign: query.precision === "year" ? null : getZodiacSign(query.birthday.getDate(), query.birthday.getMonth() + 1),
      chineseZodiacSign: getChineseZodiac(new Date(query.birthday).getFullYear())
    };

    if (result === null && !["ar"].includes(locale)) {
      metaImages.push(`https://static.countlike.pro/age-calculator-${locale}.webp`);
    } else {
      const birthYear = query === null ? null : (new Date(query.birthday)).getFullYear();

      if (query !== null
        && birthYear !== null
        && ["month", "year"].includes(query.precision)
        && birthYear > 1934
        && birthYear < 2025
        && ["ru", "en", "es", "pt", "de"].includes(locale)) {
        metaImages.push(`https://static.countlike.pro/age-how-old-am-i-if-born-in-${birthYear}-${locale}.webp`);
      }
    }
  }

  const faq = query === null ? [
    { q: t(`${TKEY}.info_h_how_it_works`), a: t(`${TKEY}.info_p_how_it_works`) },
    { q: t(`${TKEY}.info_h_why`), a: t(`${TKEY}.info_p_why`) },
  ] : [];

  return {
    query,
    result,
    diffMilliSeconds,
    celebrities: celebrities[locale] ?? [],
    meaningOfAgeContent,
    faq,
    meta: {
      shouldIndex,
      locale,
      canonical: getBaseUrl() + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING,
      currentUrl: request.url,
      title: query
        ? createTitleFromQuery(query, locale, t) + " — " + t(`${TKEY}.title`)
        : t(`${TKEY}.title`) + " — " + t(`catalog.categories.${CATEGORY}`),
      h1: query ? createTitleFromQuery(query, locale, t) : t(`${TKEY}.title`),
      description: metaDescription,
      breadcrumbs,
      images: metaImages,
    }
  };
};

function calculate(e: FormEvent<HTMLFormElement>) {
  e.preventDefault();

  const formData = new FormData(e.currentTarget);

  // Use formData to get your values, e.g., number, unit, direction, date
  const birthday = new Date(formData.get("birthday") as string);
  const atFD = formData.get("at") as string | null;
  const at = atFD ? new Date(atFD) : null;

  // Assume you have a "createSlug" function that accepts `resultingDate` as parameter
  const slug = createSlug(birthday, at);

  // Analyze the current URL and replace or add the slug accordingly
  let currentPathname = window.location.pathname;
  const slugPattern = new RegExp(`/${ROUTE_ENDING}/?(.*)$`);

  if (slugPattern.test(currentPathname)) {
    // Replace existing slug
    currentPathname = currentPathname.replace(slugPattern, `/${ROUTE_ENDING}/${slug}`);
  } else {
    // Add slug to the pathname
    currentPathname = currentPathname.endsWith("/") ? `/${ROUTE_ENDING}/${slug}` : `/${ROUTE_ENDING}/${slug}/`;
  }

  // Redirect to the new path if it changed
  const newUrl = window.location.origin + currentPathname;
  if (window.location.href === newUrl) {
    return;
  }

  window.location.href = newUrl;
}

function getQueryFromPath(path: string): Query | null {
  const lastPathPart = path.split(`/`).pop();

  const slug = (!lastPathPart || lastPathPart === ROUTE_ENDING)
    ? null
    : lastPathPart
  ;

  // Parse a query from the slug
  const query = slug === null ? null : parseInput(slug);
  console.debug("Parsed query", query);

  return query;
}

export default function Index() {
  const {
    meta,
    query,
    result,
    meaningOfAgeContent,
    celebrities
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const { t:tDates } = useTranslation("dates");

  const locale = getLocale(useLocation().pathname);
  invariant(locale);


  const birthYear = query === null ? null : (new Date(query.birthday)).getFullYear();

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.h1}
          description={meta.description}
        />
        {result !== null && query !== null ? (
          <Result color="positive">
            <H2 className="first-letter:capitalize text-lg">{t("answer")}:</H2>

            {{
              "year": <ResultWithYearPrecision locale={locale} query={query} result={result} />,
              "month": <ResultWithMonthPrecision locale={locale} query={query} result={result} />,
              "date": <ResultWithExactDatePrecision locale={locale} query={query} result={result} />
            }[query.precision] || null}

            <GenerationInfo generation={result.generation} />

            {result.zodiacSign === null ? (
              <p dangerouslySetInnerHTML={{
                __html: t(`${TKEY}.result_chinese_zodiac`, { sign: tDates(`chinese_zodiac.${result.chineseZodiacSign}`) })
              }} />
            ) : (
              <p dangerouslySetInnerHTML={{
                __html: t(`${TKEY}.result_both_zodiacs`, {
                  chinese: tDates(`chinese_zodiac.${result.chineseZodiacSign}`),
                  western: tDates(`zodiac.${result.zodiacSign}`)
                })
              }} />
            )}
          </Result>
        ) : null}


        <ToolForm onSubmit={calculate}>
          <div className="w-full xl:w-auto flex flex-col md:flex-row md:items-center md:justify-start gap-4">
            <span className="font-semibold">{t(`${TKEY}.question`)}</span>

            <div>
              <DatePicker
                name="birthday"
                value={query?.birthday ? new Date(query.birthday) : new Date("01-01-2000")}
                ariaLabel={t(`${TKEY}.input_date`)}
              />
            </div>
          </div>

          <div className="w-full xl:w-auto flex  flex-col md:flex-row md:items-center md:justify-start gap-4">
            <span className="font-semibold">{t(`${TKEY}.at_date`)}</span>

            <div>
              <DatePicker
                name="at"
                value={query?.at ? new Date(query.at) : null}
                ariaLabel={t(`${TKEY}.input_date`)}
                placeholder={t(`${TKEY}.result_today`)}
              />
            </div>
            <span className="hidden md:inline font-semibold">?</span>
          </div>

          <Button className="w-full" isSubmit={true}>
            <Icon v="keypad" className="pr-2" /> {t("do_calculate")}
          </Button>
        </ToolForm>
      </CalculatorLayout>

      <AdditionalContentLayout>
        <AdditionalInfo>
          {result === null || result.generation === null ? null : (
            <AdditionalInfoSection
              id="generation"
              contentsText={t(`${TKEY}.info_generation_contents_text`)}
              contentsEmoji="👶"
            >
              <H2 anchor="generation">{t(`${TKEY}.generation_${result.generation}`)}</H2>
              <p>{t(`${TKEY}.generation_${result.generation}_about`)}</p>
            </AdditionalInfoSection>
          )}

          {result === null || result.zodiacSign === null || query === null ? null : (
            <AdditionalInfoSection
              id="zodiac"
              contentsText={t(`${TKEY}.info_zodiac_contents_text`)}
              contentsEmoji="♈"
            >
              <H2 anchor="zodiac">
                {tDates(`zodiac.sign_if_born`, {
                  yearMonth: new Date(query.birthday).toLocaleDateString(locale, { year: "numeric", month: "long" })
                })}: {tDates(`zodiac.${result.zodiacSign}`)}
              </H2>
              <p>{tDates(`zodiac.${result.zodiacSign}_about`)}</p>
              <p dangerouslySetInnerHTML={{ __html: tDates(`zodiac.about`) }} />
            </AdditionalInfoSection>
          )}

          {result === null || query === null ? null : (
            <AdditionalInfoSection
              id="chinese-zodiac"
              contentsText={t(`${TKEY}.info_chinese_zodiac_contents_text`)}
              contentsEmoji="🐲"
            >
              <H2
                anchor="chinese-zodiac">{tDates(`chinese_zodiac.sign_if_born`, { year: birthYear })}: {tDates(`chinese_zodiac.${result.chineseZodiacSign}`)}</H2>
              <p>{tDates(`chinese_zodiac.${result.chineseZodiacSign}_about`)}</p>
              <p dangerouslySetInnerHTML={{ __html: tDates(`chinese_zodiac.about`) }} />
            </AdditionalInfoSection>
          )}

          {meaningOfAgeContent && result ? (
            <AdditionalInfoSection
              id="meaning-of-age"
              contentsText={t(`${TKEY}.info_meaning_of_age_contents_text`)}
              contentsEmoji="📘"
            >
              <H2>{t(`${TKEY}.info_h_meaning`, { age: result.years, count: result.years })}</H2>
              <div
                className="calculator-content-raw"
                dangerouslySetInnerHTML={{ __html: meaningOfAgeContent }}
              />
            </AdditionalInfoSection>
          ) : null}

          {result === null && !["ar"].includes(locale) ? (
            <ArticleImage
              src={`https://static.countlike.pro/age-calculator-${locale}.webp`}
              alt={t(`${TKEY}.cover_image_alt`)}
              caption={t(`${TKEY}.cover_image_alt`)}
              width={1200}
              height={686}
            />
          ) : (
            query !== null
            && birthYear !== null
            && ["month", "year"].includes(query.precision)
            && birthYear > 1934
            && birthYear < 2025
            && ["ru", "en", "es", "pt", "de"].includes(locale)
              ? (
                <ArticleImage
                  src={`https://static.countlike.pro/age-how-old-am-i-if-born-in-${birthYear}-${locale}.webp`}
                  alt={meta.title}
                  caption={meta.title + " " + t(`${TKEY}.cover_image_alt`)}
                  width={1200}
                  height={686}
                />
              ) : null
          )}

          {result !== null ? null : (
            <>
              <AdditionalInfoSection
                id="how-it-works"
                contentsText={t(`${TKEY}.info_how_it_works_contents_text`)}
                contentsEmoji="🛠️">
                <H2>{t(`${TKEY}.info_h_how_it_works`)}</H2>
                <p>{t(`${TKEY}.info_p_how_it_works`)}</p>
                <p>{t(`${TKEY}.info_example_1`)}</p>
              </AdditionalInfoSection>

              <AdditionalInfoSection
                id="why"
                contentsText={t(`${TKEY}.info_why_contents_text`)}
                contentsEmoji="❓"
              >
                <H2>{t(`${TKEY}.info_h_why`)}</H2>
                <p>{t(`${TKEY}.info_p_why`)}</p>

                <ul className="list-ol list-disc list-inside">
                  <li dangerouslySetInnerHTML={{
                    __html: t(`${TKEY}.info_why_p2_why_li1`)
                  }} />
                  <li dangerouslySetInnerHTML={{
                    __html: t(`${TKEY}.info_why_p2_why_li2`)
                  }} />
                  <li dangerouslySetInnerHTML={{
                    __html: t(`${TKEY}.info_why_p2_why_li3`)
                  }} />
                  <li dangerouslySetInnerHTML={{
                    __html: t(`${TKEY}.info_why_p2_why_li4`)
                  }} />
                </ul>
                <p>{t(`${TKEY}.info_why_p3`)}</p>
              </AdditionalInfoSection>

              <AdditionalInfoSection
                id="famous-people"
                contentsText={t(`${TKEY}.info_famous_people_contents_text`)}
                contentsEmoji="⭐"
              >
                <H2>{t(`${TKEY}.info_h_famous_people`)}</H2>
                <p>{t(`${TKEY}.info_p_famous_people`)}</p>
                <ul>
                  {celebrities.map((person) => (
                    <li key={person.name + person.dob}>
                      <Link to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/if-born-${person.dob}`} rel="nofollow">
                        {person.dead
                          ? t(`${TKEY}.info_how_old_is_name_dead`, { name: person.name })
                          : t(`${TKEY}.info_how_old_is_name`, { name: person.name })
                        }
                      </Link>
                    </li>
                  ))}
                </ul>
              </AdditionalInfoSection>

            </>
          )}
          {meta.shouldIndex.yandex === false || result === null || birthYear === null ? null : (
            <>
              <AdditionalInfoSection
                id="born-in-month"
                contentsText={t(`${TKEY}.info_born_in_month_contents_text`)}
                contentsEmoji="📅"
              >
                <H2>{t(`${TKEY}.born_in_month_of_this_year`, { year: birthYear })}</H2>
                <ul className="columns-1 xl:columns-2">
                  {Array.from({ length: 12 }, (_, i) => i).map((month) => (
                    <li key={month}>
                      <Link
                        to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/if-born-in-${(new Date(birthYear, month, 2)).toLocaleString("en", {
                          month: "long"
                        }).toLowerCase()}-${birthYear}`}>
                        {t(`${TKEY}.title_at_year`, {
                          year: (new Date(birthYear, month, 2)).toLocaleString(locale, {
                            month: "long",
                            year: "numeric"
                          })
                        })}{" "}
                      </Link>
                    </li>
                  ))}
                </ul>
              </AdditionalInfoSection>
            </>
          )}
        </AdditionalInfo>

        <Author lastUpdated={LAST_UPDATED}/>
      </AdditionalContentLayout>
    </>
  );
}

const ResultWithYearPrecision: React.FC<
  {
    locale: string,
    query: Jsonify<Query>,
    result: Jsonify<Result>
  }
> = (
  {
    locale,
    query,
    result
  }
): JSX.Element => {
  const { t } = useTranslation("translations");

  return (
    <>
      <p className="pt-3 text-3xl font-bold first-letter:capitalize">
        <strong dangerouslySetInnerHTML={{
          __html: t(`${TKEY}.result_age_either_either`, {
            birthMonthYear: new Date(query.birthday).toLocaleDateString(locale, { year: "numeric", month: "long" }),
            yearOne: result.years - 1,
            yearTwo: result.years
          })
        }} />
      </p>
      <p>
        {t(`${TKEY}.result_age_either_either_min`, { count: result.years }) + " "}
        {t(`${TKEY}.result_age_either_either_max`, { count: result.years })}
      </p>
      <p>{t(`${TKEY}.result_age_either_either_precise`, { count: result.years })}</p>
    </>
  );
};

const ResultWithMonthPrecision: React.FC<
  {
    locale: string,
    query: Jsonify<Query>,
    result: Jsonify<Result>
  }
> = (
  {
    locale,
    query,
    result
  }
): JSX.Element => {
  const { t } = useTranslation("translations");

  return result.months === 0 ? (
    <>
      <p className="pt-3 text-3xl font-bold first-letter:capitalize">
        <strong>
          {t(`${TKEY}.result_age_either_either`, {
            birthMonthYear: new Date(query.birthday).toLocaleDateString(locale, { year: "numeric", month: "long" }),
            yearOne: result.years - 1,
            yearTwo: result.years
          })}
        </strong>
      </p>
      <p>If this month birth day has already passed, than it is {result.years} years old. Otherwise it
        is {result.years - 1}.</p>
      <p>For precise result, submit the birth day into the form below. </p>
    </>
  ) : (
    <>
      <p className="pt-3 text-3xl font-bold first-letter:capitalize">
        <strong dangerouslySetInnerHTML={{
          __html: t(`${TKEY}.result_age_exactly`, {
            birthday: new Date(query.birthday).toLocaleDateString(locale, { year: "numeric", month: "long" }),
            count: result.years
          })
        }} />
      </p>
    </>
  );
};

const ResultWithExactDatePrecision: React.FC<
  {
    locale: string,
    query: Jsonify<Query>,
    result: Jsonify<Result>
  }
> = (
  {
    locale,
    query,
    result
  }
): JSX.Element => {
  const { t } = useTranslation("translations");

  const createDiffString = (years: number, months: number, days: number) => {
    if (years === 0 && months === 0 && days === 0) {
      return t(`${TKEY}.result_today`);
    }

    return (years > 0 ? t(`${TKEY}.result_years`, { count: years }) : "")
      + (years > 0 && months > 0 && days > 0
          ? ", "
          : (years > 0 && (months > 0 || days > 0) ? " и " : "")
      )
      + (months > 0 ? t(`${TKEY}.result_months`, { count: months }) : "")
      + (months > 0 && days > 0 ? (" " + t(`${TKEY}.result_union_preposition`) + " ") : "")
      + (days > 0 ? t(`${TKEY}.result_days`, { count: days }) : "");
  };

  return (
    <>
      <p className="mt-6">
        <strong className="pt-3 text-2xl font-bold first-letter:capitalize">
          {query?.at
            ? t(`${TKEY}.result_age_for_date`, { date: formatLongDate(new Date(query.at), locale) })
            : t(`${TKEY}.result_age_for_today`)
          } {createDiffString(result.years, result.months, result.days)}
        </strong>.
      </p>

      {/* Следующий День рождения */}
      {result.monthsTillNextBirthday === 11 && new Date(query.birthday).getDate() === new Date().getDate() ? (
        <p>{t(`${TKEY}.result_next_birthday_is_today`)}</p>
      ) : null}

      {result.monthsTillNextBirthday === 0 && result.daysTillNextBirthday === 0 ? (
        <p>{t(`${TKEY}.result_next_birthday_is_tomorrow`)}</p>
      ) : null}

      {result.monthsTillNextBirthday === 0 && result.daysTillNextBirthday === 2 ? (
        <p>{t(`${TKEY}.result_next_birthday_is_day_after_tomorrow`)}</p>
      ) : null}

      {!(result.monthsTillNextBirthday == 0 && (result.daysTillNextBirthday >= 0 && result.daysTillNextBirthday <= 2)) ? (
        <p>
          {t(`${TKEY}.result_next_birthday`)}
          <strong>{" "}{createDiffString(0, result.monthsTillNextBirthday, result.daysTillNextBirthday)}</strong>.
        </p>
      ) : null}

      <p className="mt-4">
        {t(`${TKEY}.result_total_lived_in_months`)} <strong> {createDiffString(0, result.totalMonths, 0)}</strong>.
      </p>
      <p className="mt-4">
        {t(`${TKEY}.result_total_lived_in_days`)} <strong>{createDiffString(0, 0, result.totalDays)}</strong>.
      </p>
    </>
  );
};


const GenerationInfo: React.FC<{ generation: string | null }> = ({ generation }): JSX.Element | null => {
  const { t } = useTranslation("translations");

  return generation === null ? null : (
    <p dangerouslySetInnerHTML={{
      __html: t(`${TKEY}.result_generation`, { generation: t(`${TKEY}.generation_${generation}`) })
    }} />
  );
};